import { of as observableOf, Observable, of, forkJoin } from 'rxjs';

import { catchError, map, shareReplay } from 'rxjs/operators';
import _isString from 'lodash-es/isString';
import _toString from 'lodash-es/toString';

import { Injectable } from '@angular/core';
import { IPermissions } from "../model/permission/IPermission";
import { ApiResourceService } from '../../apiResource/apiResource.service';
import { UserAuthService } from 'libraries/service/user-auth/user-auth.service';
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { TGetBookingResponse } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { EAirBookingPermissions } from "libraries/component/booking-information/e-air-booking-permissions.enum";
import { IQuote } from "libraries/service/resources/airTrip/IGetAirTripQuotes";

const configurationDefault: IConfigurationPermissionExclusion[] = [
    {
        permissionCode: EAirBookingPermissions.SHARED_AIR_BOOKING_CANCEL,
        noPermissionIfBooking: {
            host: 'TRAVELFUSION',
        }
    },
    {
        permissionCode: EAirBookingPermissions.SHARED_AIR_BOOKING_SEGMENT_MODIFY,
        noPermissionIfBooking: {
            host: 'TRAVELFUSION',
        }
    },
    {
        permissionCode: EAirBookingPermissions.SHARED_AIR_BOOKING_VOID,
        noPermissionIfBooking: {
            host: 'TRAVELFUSION',
        }
    },
    {
        permissionCode: EAirBookingPermissions.SHARED_AIR_BOOKING_DETAIL_FARE_RULES_SEARCH,
        noPermissionIfBooking: {
            host: 'TRAVELFUSION',
        }
    },
];

export interface IConfigurationPermissionExclusion {
    permissionCode: string;
    noPermissionIfBooking?: IConfigurationPermissionExclusionIf;
    noPermissionIfQuote?: IConfigurationPermissionExclusionIf;
}

export interface IConfigurationPermissionExclusionIf {
    host?: string;
    carrier?: string;
}

@Injectable({
    providedIn: 'root',
})
export class PermissionResourceService {
    private permissions: Observable<Set<string>>;
    private userId: string;

    constructor(
        private readonly apiResourceService: ApiResourceService,
        private readonly userAuthService: UserAuthService,
        private readonly configurationResourceService: ConfigurationResourceService,
    ) {
    }

    //METHODS
    /**
     * Test if a permission is associated to a user
     */
    public hasPermission(path: string): Observable<boolean> {
        return this.getPermissions().pipe(
            map((permissions: Set<string>): boolean => {
                return permissions.has(path);
            }));
    }

    /**
     * Test a permission AND use restrictions
     * https://projets2.afidium.com/issues/31594
     */
    public hasPermissionWithRestriction(path: string, booking?: TGetBookingResponse, quote?: IQuote): Observable<boolean> {
        return forkJoin([
            this.getPermissions(),
            this.configurationResourceService.get(['wui', 'config', 'permission-restriction', 'permission-restriction.json'], configurationDefault)
        ])
            .pipe(
                map(([permissions, configuration]: [Set<string>, IConfigurationPermissionExclusion[]]): boolean => {

                    const hasBooking: boolean = Boolean(booking);
                    const hasQuote: boolean = Boolean(quote);

                    if (hasBooking || hasQuote) {
                        // several restriction could be related to one path, future evolution
                        const relatedRestrictions: IConfigurationPermissionExclusion[] = configuration
                            .filter((item: IConfigurationPermissionExclusion): boolean => item.permissionCode === path);

                        if (relatedRestrictions?.length) {
                            //test only hosts for now

                            const testedHost: string = hasBooking
                                ? booking.host.toLowerCase()
                                : hasQuote ?
                                    quote.host.toLowerCase()
                                    : null;

                            const onlyHosts: string[] = relatedRestrictions
                                .filter((restriction: IConfigurationPermissionExclusion): boolean => {
                                    const restrictionIf: IConfigurationPermissionExclusionIf = this.extractPermissionIf(restriction, hasBooking, hasQuote);
                                    return Boolean(restrictionIf) && Boolean(restrictionIf.host && !restrictionIf.carrier);
                                })
                                .map((restriction: IConfigurationPermissionExclusion): string => {
                                    return this.extractPermissionIf(restriction, hasBooking, hasQuote)?.host.toLowerCase();
                                });

                            if (onlyHosts?.length && testedHost && onlyHosts.includes(testedHost)) {
                                return false;
                            }
                        }
                        return permissions.has(path);
                    }
                    return permissions.has(path);
                }),
            );
    }


    /**
     * Get permission codes set with SAM API
     */
    public getPermissions(removedPermissions?: string[]): Observable<Set<string>> {

        //here get id from authService
        const userId: string = _toString(this.userAuthService.getUserId());
        if (!userId) {
            return of(new Set());
        }

        if (!this.permissions || (this.userId !== userId) || removedPermissions?.length) { // no Permission or not the same id
            this.userId = userId;

            this.permissions = this.apiResourceService.getOne<IPermissions>(
                this.apiResourceService.getApiUrl('sam', 'users', userId, 'permissions'))
                .pipe(
                    map((response: IPermissions) => {

                        let keys: string[] = Object.keys(response);

                        const canManagePermissions: boolean = keys.includes(('dev.permissions.remove'));

                        if (removedPermissions?.length && canManagePermissions) {

                            keys = keys.filter((key: string): boolean => !removedPermissions.includes(key));

                        }
                        return new Set<string>(keys);
                    }),
                    catchError(() => {
                        return observableOf(new Set<string>());
                    }),
                    shareReplay(1)
                );
        }

        if (sessionStorage.getItem('removedPermissions') && !removedPermissions?.length) {
            return this.getPermissions(sessionStorage.getItem('removedPermissions').split(','));
        }

        return this.permissions;
    }

    public getPermissionsStartingWith(path: string[]): Observable<Set<string>> {
        return this.getPermissions()
            .pipe(
                map((allPermissions: Set<string>): Set<string> => {
                    const matchingPermissions: string[] = Array.from(allPermissions)
                        .filter((permission: string) => {
                            const permissionSubPath: string[] = permission.split('.').slice(0, path.length);

                            return permissionSubPath.every((element: string, index: number) => element === path[index]);
                        });

                    return new Set(matchingPermissions);
                }),
            );
    }

    private extractPermissionIf(restriction: IConfigurationPermissionExclusion, hasBooking: boolean, hasQuote: boolean): IConfigurationPermissionExclusionIf {
        return hasBooking
            ? restriction.noPermissionIfBooking
            : hasQuote
                ? restriction.noPermissionIfQuote
                : null;
    }
}


